import React, { Component } from 'react';
import { any, array, arrayOf, bool, number, object, oneOfType, shape, string } from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { getCustomCSSPropertiesFromConfig } from '../../util/style';
import { useIntl, intlShape } from '../../util/reactIntl';
import { metaTagProps } from '../../util/seo';
import { canonicalRoutePath } from '../../util/routes';
import { propTypes } from '../../util/types';
import { apiBaseUrl, createUserWithIdp, linkIdpToExistingAccount, loginWithIdp } from '../../util/api';
import { trackFbEvent } from '../../util/analytics';

import css from './Page.module.css';

// Module-level flags so they persist across re-renders and re-mounts
let outsetaLoginProcessed = false;
let fbInitiateCheckoutFired = false;
let fbPurchaseFired = false;

const preventDefault = e => {
  e.preventDefault();
};

const twitterPageURL = siteTwitterHandle => {
  if (siteTwitterHandle && siteTwitterHandle.charAt(0) === '@') {
    return `https://twitter.com/${siteTwitterHandle.substring(1)}`;
  } else if (siteTwitterHandle) {
    return `https://twitter.com/${siteTwitterHandle}`;
  }
  return null;
};

const webmanifestURL = marketplaceRootURL => {
  // Note: on localhost (when running "yarn run dev"), the webmanifest is running on apiServer port
  const baseUrl = apiBaseUrl(marketplaceRootURL);
  return `${baseUrl}/site.webmanifest`;
};

const getFaviconVariants = config => {
  // We add favicon through hosted configs
  // NOTE: There's no favicon.ico file. This is an imageAsset object which is used together with <meta> tags.
  const favicon = config.branding.favicon;
  return favicon?.type === 'imageAsset' ? Object.values(favicon.attributes.variants) : [];
};

const getAppleTouchIconURL = config => {
  // The appIcon is used to pick apple-touch-icon
  // We use 180x180. I.e. we follow the example set by realfavicongenerator
  const appIcon = config.branding.appIcon;
  const appIconVariants =
    appIcon?.type === 'imageAsset' ? Object.values(appIcon.attributes.variants) : [];
  const appleTouchIconVariant = appIconVariants.find(variant => {
    return variant.width === 180 && variant.height === 180;
  });
  return appleTouchIconVariant?.url;
};

class PageComponent extends Component {
  constructor(props) {
    super(props);
    // Keeping scrollPosition out of state reduces rendering cycles (and no bad states rendered)
    this.scrollPosition = 0;
    this.contentDiv = null;
    this.scrollingDisabledChanged = this.scrollingDisabledChanged.bind(this);
    this.handleLinkAccount = this.handleLinkAccount.bind(this);
    this.state = {
      // Pending Outseta link data — set when server returns account-exists-idp-not-linked
      pendingLink: null, // { email, idpToken, idpId, idpClientId }
      linkPassword: '',
      linkError: null,
      linkInProgress: false,
    };
  }

  componentDidMount() {
    // By default a dropped file is loaded in the browser window as a
    // file URL. We want to prevent this since it might loose a lot of
    // data the user has typed but not yet saved. Preventing requires
    // handling both dragover and drop events.
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    // Remove duplicated server-side rendered page schema.
    // It's in <body> to improve initial rendering performance,
    // but after web app is initialized, react-helmet-async operates with <head>
    const pageSchema = document.getElementById('page-schema');
    if (pageSchema) {
      pageSchema.remove();
    }

    // Reset on each mount so fresh page loads can trigger login
    outsetaLoginProcessed = false;

    this.initOutsetaHandler();
  }

  initOutsetaHandler() {
    if (typeof window === 'undefined') return;

    // Outseta loads asynchronously — poll until ready, up to 10 times (every 200ms)
    if (!window.Outseta) {
      let attempts = 0;
      const poll = setInterval(() => {
        attempts += 1;
        if (window.Outseta) {
          clearInterval(poll);
          this.setupOutsetaListeners();
        } else if (attempts >= 10) {
          clearInterval(poll);
          // Dev mode fallback: inject script dynamically
          if (process.env.REACT_APP_OUTSETA_DOMAIN && !document.querySelector('script[data-options]')) {
            const s = document.createElement('script');
            s.src = 'https://cdn.outseta.com/outseta.min.js';
            s.setAttribute('data-options', 'o_options');
            window.o_options = { domain: process.env.REACT_APP_OUTSETA_DOMAIN };
            document.head.appendChild(s);
            s.onload = () => this.setupOutsetaListeners();
          }
        }
      }, 200);
      return;
    }

    this.setupOutsetaListeners();
  }

  setupOutsetaListeners() {
    const handleLogin = async () => {

      if (sessionStorage.getItem('outsetaLoginComplete')) {
        sessionStorage.removeItem('outsetaLoginComplete');
        return;
      }

      if (outsetaLoginProcessed) return;
      outsetaLoginProcessed = true;

      const hasUrlToken = new URLSearchParams(window.location.search).has('access_token');

      // Guard: if there's no URL token and the browser already holds a user-scoped Sharetribe
      // session cookie, this is Outseta's widget re-initialising on a route change — not a
      // genuine login event. Returning early prevents the redirect-to-/ loop on inner pages.
      const hasUserScopedSession = () => {
        try {
          const match = document.cookie
            .split(';')
            .map(c => c.trim())
            .find(c => /^st-.+-token=/.test(c));
          if (!match) return false;
          const parsed = JSON.parse(decodeURIComponent(match.slice(match.indexOf('=') + 1)));
          return parsed.scope === 'user';
        } catch {
          return false;
        }
      };
      if (!hasUrlToken && hasUserScopedSession()) {
        outsetaLoginProcessed = false;
        return;
      }

      // Get the token from URL params or from Outseta's stored token
      const idpToken = hasUrlToken
        ? new URLSearchParams(window.location.search).get('access_token')
        : window.Outseta?.getAccessToken();

      if (!idpToken) {
        // Auth module may not be ready yet - retry after a short delay
        outsetaLoginProcessed = false;
        setTimeout(() => handleLogin(), 300);
        return;
      }

      try {
        if (!idpToken) {
          outsetaLoginProcessed = false;
          return;
        }
        const profile = await window.Outseta.getUser();

        const idpId = process.env.REACT_APP_OUTSETA_IDP_ID || 'outseta';
        const idpClientId = process.env.REACT_APP_OUTSETA_CLIENT_ID;

        try {
          await createUserWithIdp({
            idpToken,
            idpId,
            idpClientId,
            email: profile.Email,
            firstName: profile.FirstName,
            lastName: profile.LastName,
          });
        } catch (e) {
          // 409 means the account exists but has no Outseta IDP linked yet.
          // Show the "link your account" modal so the user can enter their password.
          if (e?.status === 409 || e?.code === 'account-exists-idp-not-linked') {
            this.setState({
              pendingLink: { email: profile.Email, idpToken, idpId, idpClientId },
              linkPassword: '',
              linkError: null,
            });
            return;
          }
          console.error('[Outseta] Login handler error:', e);
          outsetaLoginProcessed = false;
          return;
        }

        // Establish the authenticated Sharetribe session (scope: "user" cookie).
        // createUserWithIdp creates the user record but does not always call
        // sdk.loginWithIdp internally, so we do it explicitly here.
        try {
          await loginWithIdp({ idpToken, idpId, idpClientId });
        } catch (e) {
          console.error('[Outseta] login-with-idp error:', e);
          outsetaLoginProcessed = false;
          return;
        }

        // Ensure Outseta session cookie is set so Circle SSO works
        if (window.Outseta?.setAccessToken) {
          window.Outseta.setAccessToken(idpToken);
        }

        // Set a flag in sessionStorage to prevent re-processing on the redirect
        sessionStorage.setItem('outsetaLoginComplete', '1');
        window.location.href = '/';
      } catch (e) {
        console.error('[Outseta] Login handler error:', e);
        outsetaLoginProcessed = false;
      }
    };

    window.Outseta.on('signup.registrationData', (formData, planData) => {
      if (!planData?.Plan || fbInitiateCheckoutFired) return;
      fbInitiateCheckoutFired = true;
      const term = planData.BillingRenewalTerm;
      const value =
        term === 3 ? planData.Plan.AnnualRate
        : term === 2 ? planData.Plan.QuarterlyRate
        : planData.Plan.MonthlyRate;
      trackFbEvent('InitiateCheckout', {
        currency: 'GBP',
        value,
        content_ids: [planData.Plan.Uid],
        content_name: planData.Plan.Name,
        content_type: 'product',
        num_items: 1,
      });
    });

    window.Outseta.on('signup', account => {
      if (!account?.CurrentSubscription || fbPurchaseFired) return;
      fbPurchaseFired = true;
      const plan = account.CurrentSubscription.Plan;
      const value = account.CurrentSubscription.Rate || plan?.MonthlyRate || 0;
      const params = {
        currency: 'GBP',
        value,
        content_ids: [plan?.Uid].filter(Boolean),
        content_name: plan?.Name,
        content_type: 'product',
      };
      trackFbEvent('CompleteRegistration', params);
      trackFbEvent('Purchase', { ...params, transaction_id: account.Uid });
    });

    window.Outseta.on('accessToken.set', handleLogin);

    // Handle race condition: if Outseta already processed the URL token
    // before our listener was registered (happens in Chrome), call handleLogin now
    const hasUrlToken = new URLSearchParams(window.location.search).has('access_token');
    if (hasUrlToken && window.Outseta.getAccessToken()) {
      handleLogin();
    }
  }

  async handleLinkAccount(e) {
    e.preventDefault();
    const { pendingLink, linkPassword } = this.state;
    if (!pendingLink || !linkPassword) return;

    this.setState({ linkInProgress: true, linkError: null });

    try {
      await linkIdpToExistingAccount({
        email: pendingLink.email,
        password: linkPassword,
        idpToken: pendingLink.idpToken,
        idpId: pendingLink.idpId,
        idpClientId: pendingLink.idpClientId,
      });
      window.location.href = '/';
    } catch (err) {
      const isWrongPassword = err?.code === 'wrong-password' || err?.status === 401;
      this.setState({
        linkInProgress: false,
        linkError: isWrongPassword
          ? 'Incorrect password. Please try again.'
          : 'Something went wrong. Please try again.',
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('dragover', preventDefault);
    document.removeEventListener('drop', preventDefault);
  }

  scrollingDisabledChanged(currentScrollingDisabled) {
    if (currentScrollingDisabled && currentScrollingDisabled !== this.scrollingDisabled) {
      // Update current scroll position, if scrolling is disabled (e.g. modal is open)
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      this.scrollingDisabled = currentScrollingDisabled;
    } else if (currentScrollingDisabled !== this.scrollingDisabled) {
      this.scrollingDisabled = currentScrollingDisabled;
    }
  }

  render() {
    const {
      className,
      rootClassName,
      children,
      location,
      intl,
      scrollingDisabled,
      referrer,
      author,
      openGraphType = 'website',
      description,
      facebookImages,
      published,
      schema,
      socialSharing,
      title,
      twitterHandle,
      twitterImages,
      updated,
      config,
      routeConfiguration,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className, {
      [css.scrollingDisabled]: scrollingDisabled,
    });

    this.scrollingDisabledChanged(scrollingDisabled);

    const marketplaceRootURL = config.marketplaceRootURL;
    const shouldReturnPathOnly = referrer && referrer !== 'unsafe-url';
    const canonicalPath = canonicalRoutePath(routeConfiguration, location, shouldReturnPathOnly);
    const canonicalUrl = `${marketplaceRootURL}${canonicalPath}`;

    const marketplaceName = config.marketplaceName;
    const schemaTitle = intl.formatMessage({ id: 'Page.schemaTitle' }, { marketplaceName });
    const schemaDescription = intl.formatMessage({ id: 'Page.schemaDescription' });
    const pageTitle = title || schemaTitle;
    const pageDescription = description || schemaDescription;
    const {
      title: socialSharingTitle,
      description: socialSharingDescription,
      images1200: socialSharingImages1200,
      // Note: we use image with open graph's aspect ratio (1.91:1) also with Twitter
      images600: socialSharingImages600,
    } = socialSharing || {};

    // Images for social media sharing
    const defaultFacebookImageURL = config.branding.facebookImage;
    const openGraphFallbackImages = [
      {
        name: 'facebook',
        url: defaultFacebookImageURL,
        width: 1200,
        height: 630,
      },
    ];
    const defaultTwitterImageURL = config.branding.twitterImage;
    const twitterFallbackImages = [
      {
        name: 'twitter',
        url: defaultTwitterImageURL,
        width: 600,
        height: 314,
      },
    ];
    const facebookImgs = socialSharingImages1200 || facebookImages || openGraphFallbackImages;
    const twitterImgs = socialSharingImages600 || twitterImages || twitterFallbackImages;

    const metaToHead = metaTagProps(
      {
        author,
        openGraphType,
        socialSharingTitle: socialSharingTitle || pageTitle,
        socialSharingDescription: socialSharingDescription || pageDescription,
        description: pageDescription,
        facebookImages: facebookImgs,
        twitterImages: twitterImgs,
        twitterHandle,
        published,
        updated,
        url: canonicalUrl,
        locale: intl.locale,
      },
      config
    );

    const facebookPage = config.siteFacebookPage;
    const twitterPage = twitterPageURL(config.siteTwitterHandle);
    const instagramPage = config.siteInstagramPage;
    const sameOrganizationAs = [facebookPage, twitterPage, instagramPage].filter(v => v != null);

    // Schema for search engines (helps them to understand what this page is about)
    // http://schema.org
    // We are using JSON-LD format

    // Schema attribute can be either single schema object or an array of objects
    // This makes it possible to include several different items from the same page.
    // E.g. Product, Place, Video
    const hasSchema = schema != null;
    const schemaFromProps = hasSchema && Array.isArray(schema) ? schema : hasSchema ? [schema] : [];
    const addressMaybe = config.address?.streetAddress ? { address: config.address } : {};
    const schemaArrayJSONString = JSON.stringify({
      '@context': 'http://schema.org',
      '@graph': [
        ...schemaFromProps,
        {
          '@context': 'http://schema.org',
          '@type': 'Organization',
          '@id': `${marketplaceRootURL}#organization`,
          url: marketplaceRootURL,
          name: marketplaceName,
          sameAs: sameOrganizationAs,
          logo: config.branding.logoImageMobileURL,
          ...addressMaybe,
        },
        {
          '@context': 'http://schema.org',
          '@type': 'WebSite',
          url: marketplaceRootURL,
          description: schemaDescription,
          name: schemaTitle,
        },
      ],
    });

    const scrollPositionStyles = scrollingDisabled
      ? { marginTop: `${-1 * this.scrollPosition}px` }
      : {};

    // If scrolling is not disabled, but content element has still scrollPosition set
    // in style attribute, we scrollTo scrollPosition.
    const hasMarginTopStyle = this.contentDiv && this.contentDiv.style.marginTop;
    if (!scrollingDisabled && hasMarginTopStyle) {
      window.requestAnimationFrame(() => {
        window.scrollTo(0, this.scrollPosition);
      });
    }

    const faviconVariants = getFaviconVariants(config);
    const appleTouchIcon = getAppleTouchIconURL(config);

    // Marketplace color and the color for <PrimaryButton> come from configs
    // If set, we need to create those custom CSS Properties and set them for the app
    // Note: this is also set to <html> element in app.js to provide marketplace colors for modals/portals.
    const styles = getCustomCSSPropertiesFromConfig(config.branding);

    const { pendingLink, linkPassword, linkError, linkInProgress } = this.state;

    return (
      <div id="page" className={classes} style={styles}>
        {pendingLink ? (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}>
            <div style={{
              background: '#fff', borderRadius: 8, padding: '32px 28px',
              width: '100%', maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            }}>
              <h2 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 700 }}>
                Connect your account
              </h2>
              <p style={{ margin: '0 0 20px', color: '#555', fontSize: 15 }}>
                An account for <strong>{pendingLink.email}</strong> already exists.
                Enter your current password to link it with Outseta.
              </p>
              <form onSubmit={this.handleLinkAccount}>
                <input
                  type="password"
                  placeholder="Current password"
                  value={linkPassword}
                  onChange={e => this.setState({ linkPassword: e.target.value })}
                  style={{
                    width: '100%', padding: '10px 12px', fontSize: 15,
                    border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box',
                    marginBottom: 8,
                  }}
                  autoFocus
                  required
                />
                {linkError ? (
                  <p style={{ color: '#c0392b', fontSize: 14, margin: '0 0 8px' }}>{linkError}</p>
                ) : null}
                <button
                  type="submit"
                  disabled={linkInProgress || !linkPassword}
                  style={{
                    width: '100%', padding: '11px 0', fontSize: 15, fontWeight: 600,
                    background: '#222', color: '#fff', border: 'none', borderRadius: 4,
                    cursor: linkInProgress ? 'not-allowed' : 'pointer', marginTop: 4,
                  }}
                >
                  {linkInProgress ? 'Connecting…' : 'Connect account'}
                </button>
                <button
                  type="button"
                  onClick={() => { this.setState({ pendingLink: null }); outsetaLoginProcessed = false; }}
                  style={{
                    width: '100%', padding: '9px 0', fontSize: 14, marginTop: 8,
                    background: 'transparent', color: '#777', border: 'none', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </form>
            </div>
          </div>
        ) : null}
        <Helmet
          htmlAttributes={{
            lang: intl.locale,
          }}
        >
          <title>{pageTitle}</title>
          {referrer ? <meta name="referrer" content={referrer} /> : null}
          <link rel="canonical" href={canonicalUrl} />

          {faviconVariants.map(variant => {
            return (
              <link
                key={`icon_${variant.width}`}
                rel="icon"
                type="image/png"
                sizes={`${variant.width}x${variant.height}`}
                href={variant.url}
              />
            );
          })}

          {appleTouchIcon ? (
            <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIcon} />
          ) : null}

          <link rel="manifest" href={webmanifestURL(marketplaceRootURL)} />

          <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8" />
          <meta httpEquiv="Content-Language" content={intl.locale} />
          {metaToHead.map((metaProps, i) => (
            <meta key={i} {...metaProps} />
          ))}
          <script id="page-schema" type="application/ld+json">
            {schemaArrayJSONString.replace(/</g, '\\u003c')}
          </script>

        </Helmet>
        <div
          className={css.content}
          style={scrollPositionStyles}
          ref={c => {
            this.contentDiv = c;
          }}
        >
          {children}
        </div>
      </div>
    );
  }
}

/**
 * @typedef {Object} ImageConfig
 * @property {string} width - The width of the image
 * @property {string} height - The height of the image
 * @property {string} url - The URL of the image
 */
/**
 * @typedef {Object} SocialSharingConfig
 * @property {string} title - The title for the social sharing service
 * @property {string} description - The description for the social sharing service
 * @property {Array<ImageConfig>} images1200 - The images for social sharing (1200x630)
 * @property {Array<ImageConfig>} images600 - The images for social sharing (600x314)
 */
/**
 * A component that renders a page.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {ReactNode} props.children - The children to render
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {string} props.referrer - Handle referrer policy
 * @param {string} props.author - The author
 * @param {string} props.openGraphType - The open graph type (aka 'og:type')
 * @param {string} props.description - The description
 * @param {Array<ImageConfig>} props.facebookImages - The facebook images
 * @param {Array<ImageConfig>} props.twitterImages - The twitter images
 * @param {string} props.published - The published date (article:published_time)
 * @param {object|array} props.schema - The schema from
 * @param {SocialSharingConfig} props.socialSharing - The social sharing
 * @param {string} props.title - The page title
 * @param {string} props.twitterHandle - The twitter handle
 * @param {string} props.updated - The updated date article:modified_time
 * @returns {JSX.Element} Page component that handles SEO and social sharing
 */
const Page = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const location = useLocation();
  const intl = useIntl();

  return (
    <PageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      location={location}
      intl={intl}
      {...props}
    />
  );
};

export default Page;
